import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, SlidersHorizontal, ChevronRight } from "lucide-react";

import { useApp } from "@/App";
import { students, type Student } from "@/data/mock";
import { school } from "@/theme/school";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — student roster.
//
// The only screen in the demo with real interactive filtering.
// The search field filters by name, class, or guardian; the
// class-chip row filters by class. Both are live, both are
// memoised, and both produce a stable empty state when nothing
// matches.
//
// Kept deliberately one screen. A student detail view would be
// natural, but it's not in the tree and inventing one here would
// push this file past the size where it stays reviewable.
// ─────────────────────────────────────────────────────────────────

const CLASSES = ["All", "S1", "S2", "S3", "S4"] as const;
type ClassFilter = (typeof CLASSES)[number];

export default function Students() {
  const name = useApp((s) => s.name);
  const role = useApp((s) => s.role);

  const [query, setQuery] = useState("");
  const [klass, setKlass] = useState<ClassFilter>("All");
  const [focused, setFocused] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return students.filter((s) => {
      if (klass !== "All" && s.klass !== klass) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.klass.toLowerCase().includes(q) ||
        s.guardian.toLowerCase().includes(q)
      );
    });
  }, [query, klass]);

  const canSearch = role === "director" || role === "bursar" || role === "teacher";

  return (
    <div className="px-5 pb-8 pt-5">
      {/* ─── Page heading ──────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <p className="text-ios-footnote text-ios-secondary">
          {school.name} · Term 2, 2026
        </p>
        <h1 className="mt-0.5 text-ios-title-1 font-bold tracking-ios text-ios-label">
          {students.length} students
        </h1>
      </motion.div>

      {/* ─── Search ──────────────────────────────────────────
          A real iOS search field: gray pill, magnifier leading,
          clear button trailing when non-empty. The focused state
          swaps the container's background subtly and turns on a
          focus ring — matching iOS's own search field. */}
      {canSearch && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mt-4"
        >
          <div
            className="flex items-center gap-2 rounded-ios-md px-3 py-2.5 transition-shadow"
            style={{
              backgroundColor: focused ? "#FFFFFF" : "rgba(120, 120, 128, 0.10)",
              boxShadow: focused
                ? "0 0 0 4px rgba(0, 122, 255, 0.18), 0 1px 2px rgba(0,0,0,0.04)"
                : "none",
            }}
          >
            <Search
              size={16}
              strokeWidth={2.4}
              className="shrink-0 text-ios-secondary"
              absoluteStrokeWidth
            />
            <input
              type="text"
              inputMode="search"
              placeholder="Search by name, class, guardian"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              className="min-w-0 flex-1 bg-transparent text-ios-body text-ios-label placeholder:text-ios-secondary/70 focus:outline-none"
            />
            <AnimatePresence>
              {query && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.7 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  onClick={() => setQuery("")}
                  className="ios-pressable grid h-5 w-5 shrink-0 place-items-center rounded-full"
                  style={{ backgroundColor: "rgba(60, 60, 67, 0.18)", color: "#FFFFFF" }}
                  aria-label="Clear search"
                >
                  <X size={12} strokeWidth={3} absoluteStrokeWidth />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* ─── Class chips ─────────────────────────────────────
          A horizontally scrollable filter row. The chips are
          pill-shaped, use a subtle background when inactive
          and the brand color when active. The scroll container
          has no visible scrollbar (handled globally in
          index.css) and snaps to the chip boundary. */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="-mx-5 mt-3 flex gap-2 overflow-x-auto px-5 pb-1"
        style={{ scrollSnapType: "x proximity" }}
      >
        {CLASSES.map((c) => {
          const active = klass === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setKlass(c)}
              className="ios-pressable shrink-0 rounded-ios-pill px-3.5 py-1.5 text-ios-footnote font-semibold transition-colors"
              style={{
                scrollSnapAlign: "start",
                backgroundColor: active ? "var(--brand-navy)" : "rgba(120, 120, 128, 0.10)",
                color: active ? "#FFFFFF" : "#3C3C43",
              }}
            >
              {c}
            </button>
          );
        })}
      </motion.div>

      {/* ─── Result count ────────────────────────────────────
          Only shown when a filter is active. AnimatePresence
          makes it fade in and out as the filter changes,
          avoiding the layout jump that a conditionally-
          rendered paragraph would otherwise cause. */}
      <AnimatePresence initial={false}>
        {(query || klass !== "All") && (
          <motion.p
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="overflow-hidden text-ios-caption-1 text-ios-secondary"
          >
            {filtered.length === 0
              ? "No matches"
              : `${filtered.length} match${filtered.length === 1 ? "" : "es"}`}
          </motion.p>
        )}
      </AnimatePresence>

      {/* ─── List ────────────────────────────────────────────
          A single grouped card, one row per student. Rows use
          the shared hairline separator and the iOS press
          effect. The empty state renders inside the same
          visual frame so the layout doesn't collapse. */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="mt-4 overflow-hidden rounded-ios-lg bg-ios-card shadow-ios"
      >
        {filtered.length === 0 ? (
          <EmptyState />
        ) : (
          filtered.map((s, i) => (
            <StudentRow
              key={s.id}
              student={s}
              separator={i < filtered.length - 1}
              index={Math.min(i, 8)}
            />
          ))
        )}
      </motion.div>

      <p className="mt-4 text-center text-ios-caption-2 text-ios-secondary">
        Signed in as {name}
      </p>
    </div>
  );
}

// ─── StudentRow ─────────────────────────────────────────────────

function StudentRow({
  student,
  separator,
  index,
}: {
  student: Student;
  separator: boolean;
  index: number;
}) {
  const initial = student.name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const paid = student.balance.startsWith("UGX 0");

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 4 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.22,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: index * 0.02,
        },
      }}
      className={[
        "ios-pressable flex w-full items-center gap-3 px-4 py-3 text-left",
        separator ? "hairline-b" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[12px] font-semibold"
        style={{
          backgroundColor: "rgba(10, 37, 64, 0.08)",
          color: "var(--brand-navy)",
        }}
      >
        {initial}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-ios-body font-medium text-ios-label">
          {student.name}
        </span>
        <span className="mt-0.5 block truncate text-ios-caption-1 text-ios-secondary">
          {student.klass} · {student.guardian}
        </span>
      </span>

      <span className="shrink-0 text-right">
        <span
          className="block font-tabular text-ios-footnote font-semibold"
          style={{ color: paid ? "#248A3D" : "#C93400" }}
        >
          {student.balance}
        </span>
        <span className="mt-0.5 block text-ios-caption-2 text-ios-tertiary">
          {student.attendance}%
        </span>
      </span>

      <ChevronRight
        size={16}
        strokeWidth={2.4}
        className="shrink-0 text-ios-tertiary"
        absoluteStrokeWidth
      />
    </motion.button>
  );
}

// ─── EmptyState ─────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <span
        className="grid h-10 w-10 place-items-center rounded-full"
        style={{ backgroundColor: "rgba(120, 120, 128, 0.10)", color: "#8E8E93" }}
      >
        <SlidersHorizontal size={18} strokeWidth={2} absoluteStrokeWidth />
      </span>
      <p className="text-ios-subhead font-semibold text-ios-label">No students found</p>
      <p className="max-w-[220px] text-ios-caption-1 text-ios-secondary">
        Try a different class, or clear the search to see the full roster.
      </p>
    </div>
  );
}